/**
 * Behavioral State Walker (doc 147 §6.6 E4 + PR Q4 coverage + suggestions).
 *
 * Matrix of state × mode tag combinations. PR Q4 adds:
 *  - Coverage score above the matrix (populated/total + bar).
 *  - Tag-suggestion cells dashed-bordered with candidate surfaces
 *    inferred from row/column peers.
 */
import { useMemo } from 'react';
import type { AtlasManifest, Surface } from '@atlas-manifest/types';
import {
  coverageScore,
  suggestTagGaps,
} from '../state/walkerSuggestions.js';
import { navigate } from '../state/hashRouter.js';

export interface BehavioralWalkerViewProps {
  manifest: AtlasManifest;
  focus?: string;
}

const DEFAULT_STATES = ['baseline', 'in-distress', 'thriving', 'building'];
const DEFAULT_MODES = ['observer', 'advisor', 'collaborator', 'steward'];

function tagsForPrefix(manifest: AtlasManifest, prefix: string, defaults: string[]): string[] {
  const found = new Set<string>(defaults);
  for (const e of manifest.entities) {
    for (const t of e.tags) {
      if (t.startsWith(`${prefix}:`)) {
        const suffix = t.slice(prefix.length + 1);
        if (suffix && suffix !== 'all') found.add(suffix);
      }
    }
  }
  return [...found].sort();
}

function surfacesForCell(manifest: AtlasManifest, state: string, mode: string): Surface[] {
  return manifest.surfaces.filter((s) => {
    const entity = manifest.entities.find((e) => e.surfaces.includes(s.id));
    if (!entity) return false;
    const stateMatch = entity.tags.includes(`state:${state}`) || entity.tags.includes('state:all');
    const modeMatch = entity.tags.includes(`mode:${mode}`) || entity.tags.includes('mode:all');
    return stateMatch && modeMatch;
  });
}

export function BehavioralWalkerView({
  manifest,
  focus,
}: BehavioralWalkerViewProps): React.JSX.Element {
  const states = useMemo(() => tagsForPrefix(manifest, 'state', DEFAULT_STATES), [manifest]);
  const modes = useMemo(() => tagsForPrefix(manifest, 'mode', DEFAULT_MODES), [manifest]);
  const coverage = useMemo(() => coverageScore(manifest), [manifest]);
  const suggestionsByCell = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const s of suggestTagGaps(manifest)) {
      m.set(s.cellId, s.candidateSurfaces);
    }
    return m;
  }, [manifest]);

  const coveragePct = coverage.total > 0
    ? Math.round((coverage.populated / coverage.total) * 100)
    : 0;
  const coverageColor =
    coveragePct > 70 ? 'var(--good)' : coveragePct > 30 ? 'var(--warn)' : 'var(--danger)';

  return (
    <div className="behavioral-walker">
      <h2>Behavioral State Walker</h2>
      {focus && (
        <p className="scope-banner" role="status">
          Focused on <strong>{focus}</strong> — showing the full state × mode
          matrix; surfaces are highlighted where the entity participates.
        </p>
      )}
      <p className="subtitle">
        Matrix of <code>state:*</code> × <code>mode:*</code> tag combinations
        showing which surfaces render for each (state, mode) pairing. Dashed
        cells are suggestions — peers in the same row/column hint at coverage
        the entity tags would benefit from.
      </p>

      <section className="behavioral-coverage" aria-label="Coverage score">
        <span className="behavioral-coverage-label">COVERAGE</span>
        <div className="behavioral-coverage-bar">
          <div
            className="behavioral-coverage-fill"
            style={{ width: `${coveragePct}%`, background: coverageColor }}
          />
        </div>
        <span className="behavioral-coverage-meta mono">
          {coverage.populated} / {coverage.total} cells populated · {coveragePct}%
        </span>
      </section>

      <div className="behavioral-grid-wrap">
        <table className="behavioral-grid">
          <thead>
            <tr>
              <th scope="col">state ↓ / mode →</th>
              {modes.map((m) => (
                <th key={m} scope="col">{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {states.map((s) => (
              <tr key={s}>
                <th scope="row">{s}</th>
                {modes.map((m) => {
                  const cellId = `${s}:${m}`;
                  const surfaces = surfacesForCell(manifest, s, m);
                  const suggestion = suggestionsByCell.get(cellId);
                  if (surfaces.length > 0) {
                    return (
                      <td key={cellId} className="behavioral-cell is-populated">
                        <ul>
                          {surfaces.map((sf) => (
                            <li key={sf.id} className="mono">
                              <button
                                type="button"
                                className="behavioral-surface-btn"
                                onClick={() => navigate({ kind: 'surface', id: sf.id })}
                              >
                                {sf.id}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </td>
                    );
                  }
                  if (suggestion && suggestion.length > 0) {
                    return (
                      <td
                        key={cellId}
                        className="behavioral-cell is-suggested"
                        title={`Suggested via ${suggestion.length} peer surface(s)`}
                      >
                        <div className="behavioral-cell-suggest-head">↳ suggest</div>
                        <ul>
                          {suggestion.slice(0, 2).map((sid) => (
                            <li key={sid} className="mono">
                              <button
                                type="button"
                                className="behavioral-surface-btn"
                                onClick={() => navigate({ kind: 'surface', id: sid })}
                              >
                                {sid}
                              </button>
                            </li>
                          ))}
                          {suggestion.length > 2 && (
                            <li className="mono behavioral-cell-suggest-more">
                              +{suggestion.length - 2} more
                            </li>
                          )}
                        </ul>
                      </td>
                    );
                  }
                  return (
                    <td key={cellId} className="behavioral-cell">
                      <span className="mono">—</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
